/**
 * CompatResultView — presentational compatibility result (overall score +
 * sub-score bars + report). Used by the standalone /compat/:id page. Takes a
 * plain data object (no flow state machine) so it can render a result fetched
 * by id for either party. Renders the report Markdown via ReportMarkdown.
 */
import { useTranslation } from 'react-i18next';
import type { CompatResultCheck } from '../../../hooks/useCompatResult';
import { ReportMarkdown } from '../../ReportMarkdown';
import styles from './Compat.module.css';

interface SubScore {
  labelKey: string;
  icon: string;
  key: 'scoreLove' | 'scoreFriendship' | 'scoreCommunication' | 'scoreConflict';
  color: string;
}

const SUB_SCORES: SubScore[] = [
  { labelKey: 'compat.result.sub.romantic',      icon: '♥', key: 'scoreLove',          color: '#ff8eb0' },
  { labelKey: 'compat.result.sub.friendship',    icon: '✶', key: 'scoreFriendship',    color: '#c89cff' },
  { labelKey: 'compat.result.sub.communication', icon: '◐', key: 'scoreCommunication', color: '#90caf9' },
  { labelKey: 'compat.result.sub.conflict',      icon: '⚡', key: 'scoreConflict',       color: '#e89a3c' },
];

export function CompatResultView({ output }: { output: CompatResultCheck }): JSX.Element {
  const { t } = useTranslation('flows');
  const overall = Math.round(output.scoreOverall);

  return (
    <div data-testid="compat-result-view">
      <div className={styles.heroScore}>
        <div className={styles.heroNumber}>
          {overall}<span className={styles.heroPct}>%</span>
        </div>
        <div className={styles.heroSubtext}>{t('compat.result.heroSubtext')}</div>
      </div>

      <div className={styles.subScoresSection}>
        {SUB_SCORES.map((s) => {
          const pct = Math.round(output[s.key]);
          return (
            <div key={s.key} className={styles.scoreRow}>
              <span className={styles.scoreIcon} style={{ color: s.color }}>{s.icon}</span>
              <div>
                <div className={styles.scoreLabel}>{t(s.labelKey)}</div>
                <div className={styles.scoreBarWrap}>
                  <div
                    className={styles.scoreBar}
                    style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${s.color}aa, ${s.color})` }}
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t('compat.result.scoreAria', { label: t(s.labelKey), pct })}
                  />
                </div>
              </div>
              <div className={styles.scoreValue}>
                {pct}<span className={styles.scorePctUnit}>%</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.reportSection}>
        <ReportMarkdown markdown={output.reportMarkdown ?? ''} />
      </div>
    </div>
  );
}
